import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { fetchDocumentRequests, approveDocument, rejectDocument } from '../../redux/documentUploadSlice';
import { useNotifications } from '../../hooks/useNotifications';
import DashboardLayout from '../../components/layout/DashboardLayout';
import AdminSidebar from '../../components/sidebar/AdminSidebar';
import StatusBadge from '../../components/StatusBadge';
import ApprovalWorkflowDrawer from '../../components/dashboard/ApprovalWorkflowDrawer';
import './AdminReviewPage.css';

const AdminReviewPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useNotifications();
  const { documentRequests, loading } = useSelector((state: RootState) => state.documentUpload);

  const [selectedDocument, setSelectedDocument] = useState<any>(null);

  useEffect(() => {
    dispatch(fetchDocumentRequests());
  }, [dispatch]);

  const handleApprove = async (documentId: string) => {
    try {
      await dispatch(approveDocument(documentId)).unwrap();
      notifications.success('Document approved successfully!');
      setSelectedDocument(null);
    } catch (error) {
      console.error('Failed to approve document:', error);
      notifications.error('Failed to approve document. Please try again.');
    }
  };

  const handleReject = async (documentId: string, trimmedReason: string) => {
    try {
      await dispatch(rejectDocument({ documentId, reason: trimmedReason })).unwrap();
      notifications.success('Document rejected successfully.');
      setSelectedDocument(null);
    } catch (error: any) {
      console.error('Failed to reject document:', error);

      // Show specific error message from backend if available
      if (error.response?.data?.message) {
        notifications.error(`Failed to reject document: ${error.response.data.message}`);
      } else if (error.response?.data?.errors) {
        // Handle validation errors array
        const validationErrors = error.response.data.errors;
        if (Array.isArray(validationErrors) && validationErrors.length > 0) {
          notifications.error(`Failed to reject document: ${validationErrors[0].message}`);
        } else {
          notifications.error('Failed to reject document. Please check your input and try again.');
        }
      } else {
        notifications.error('Failed to reject document. Please try again.');
      }
    }
  };

  const openReviewModal = (document: any) => {
    setSelectedDocument(document);
  };

  const closeReviewModal = () => {
    setSelectedDocument(null);
  };

  return (
    <DashboardLayout sidebar={<AdminSidebar />}>
      <div className="admin-review-page">
        <div className="page-header">
          <h2>Document Review</h2>
          <p>Review and approve/reject submitted documents</p>
        </div>

        <div className="documents-container">
          {loading ? (
            <div className="loading">
              <p>Loading document requests...</p>
            </div>
          ) : documentRequests.length === 0 ? (
            <div className="no-documents">
              <p>No document requests to review.</p>
            </div>
          ) : (
            <table className="documents-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Document Type</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documentRequests.map((request) => (
                  <tr key={request._id}>
                    <td>
                      <div className="user-info">
                        <div className="user-name">User</div>
                        <div className="company-name">Company</div>
                      </div>
                    </td>
                    <td className="document-type">
                      {request.type}
                    </td>
                    <td>
                      <StatusBadge status={request.status} type="document" />
                    </td>
                    <td className="submitted-date">
                      {request.uploadedAt ? new Date(request.uploadedAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="actions">
                      {request.status === 'pending' && (
                        <button
                          onClick={() => openReviewModal(request)}
                          className="review-btn"
                        >
                          Review in Drawer
                        </button>
                      )}
                      {request.status !== 'pending' && (
                        <span className="decision-made">
                          {request.status.toUpperCase()}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <ApprovalWorkflowDrawer
          isOpen={Boolean(selectedDocument)}
          title="Document Review Decision"
          subtitle="Inspect document evidence before confirming approval state."
          onClose={closeReviewModal}
          contextItems={
            selectedDocument
              ? [
                  { label: 'Type', value: selectedDocument.type },
                  {
                    label: 'Submitted',
                    value: selectedDocument.uploadedAt
                      ? new Date(selectedDocument.uploadedAt).toLocaleDateString()
                      : 'N/A',
                  },
                  { label: 'Source', value: selectedDocument.s3Url ? 'Uploaded file available' : 'No file URL' },
                ]
              : []
          }
          onApprove={
            selectedDocument
              ? () => handleApprove(selectedDocument._id)
              : undefined
          }
          onReject={
            selectedDocument
              ? (reason) => handleReject(selectedDocument._id, reason)
              : undefined
          }
          rejectionLabel="Review Notes"
          rejectionPlaceholder="State what is missing or invalid in this document."
        >
          {selectedDocument?.s3Url && (
            <button
              type="button"
              className="btn-secondary w-full justify-center"
              onClick={() => window.open(selectedDocument.s3Url, '_blank')}
            >
              View Uploaded Document
            </button>
          )}
        </ApprovalWorkflowDrawer>
      </div>
    </DashboardLayout>
  );
};

export default AdminReviewPage;
